import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBrain, FaBullseye, FaFolderOpen } from 'react-icons/fa';
import PromotionalJupasPredictEmbed from './promotional/PromotionalJupasPredictEmbed';
import PromotionalProgrammeRecommendationsEmbed from './promotional/PromotionalProgrammeRecommendationsEmbed';
import PromotionalAbilityMenuEmbed from './promotional/PromotionalAbilityMenuEmbed';
import PromotionalInterestEmbed from './promotional/PromotionalInterestEmbed';
import TrustLogoMarquee from './promotional/TrustLogoMarquee';
import { PROMO_DEMO_DSE_SCORES } from '../../data/promotionalHomeDemo';

const INSTITUTION_LOGOS = [
  { src: '/asset/logos/institutions/hku.png', alt: 'HKU' },
  { src: '/asset/logos/institutions/cuhk.png', alt: 'CUHK' },
  { src: '/asset/logos/institutions/hkust.png', alt: 'HKUST' },
  { src: '/asset/logos/institutions/polyu.png', alt: 'PolyU' },
  { src: '/asset/logos/institutions/cityu.png', alt: 'CityU' },
  { src: '/asset/logos/institutions/hkbu.png', alt: 'HKBU' },
  { src: '/asset/logos/institutions/eduhk.png', alt: 'EdUHK' },
  { src: '/asset/logos/institutions/lingnan.png', alt: 'Lingnan' },
  { src: '/asset/logos/institutions/hkmu.png', alt: 'HKMU' },
  { src: '/asset/logos/institutions/sfu.png', alt: 'SFU' },
];

const ArrowRightIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const BLUEPRINT_STEP_ICONS = {
  evaluate: FaFolderOpen,
  discover: FaBrain,
  align: FaBullseye,
};

/** Soft accent panel — light blue wash in light mode, muted accent in dark */
const featureCardClass =
  'rounded-3xl p-6 sm:p-8 shadow-sm border border-accent/30 bg-accent/40 dark:bg-accent/15 dark:border-accent/20';


const promoCtaClass =
  'inline-flex flex-row items-center justify-center gap-2 px-6 py-3.5 text-base font-bold rounded-full shrink-0 bg-accent text-accent-foreground transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

function TestPromoCopy({ title, description, hint, cta }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-link">{hint}</p>
      <h3 className="mt-2 text-xl sm:text-2xl font-bold text-ink">{title}</h3>
      <p className="mt-3 text-sm sm:text-base text-ink-muted leading-relaxed">{description}</p>
      <div className="mt-6">{cta}</div>
    </div>
  );
}

const TimelineStep = ({ align, iconKey, title, body }) => {
  const Icon = BLUEPRINT_STEP_ICONS[iconKey];

  return (
    <div
      className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center ${
        align === 'right' ? 'md:text-left' : 'md:text-right'
      }`}
    >
      {align === 'left' ? (
        <>
          <div className="md:pr-8 md:text-right order-2 md:order-1">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-accent/40 text-link mb-3 dark:bg-accent/20"
              aria-hidden
            >
              {Icon ? <Icon className="w-6 h-6" /> : null}
            </div>
            <h3 className="text-lg font-bold text-ink mb-2">{title}</h3>
            <p className="text-ink-muted text-sm leading-relaxed max-w-md md:ml-auto">{body}</p>
          </div>
          <div className="hidden md:block order-2" aria-hidden />
        </>
      ) : (
        <>
          <div className="hidden md:block order-1" aria-hidden />
          <div className="md:pl-8 order-2 md:order-2">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-accent/40 text-link mb-3 dark:bg-accent/20"
              aria-hidden
            >
              {Icon ? <Icon className="w-6 h-6" /> : null}
            </div>
            <h3 className="text-lg font-bold text-ink mb-2">{title}</h3>
            <p className="text-ink-muted text-sm leading-relaxed max-w-md">{body}</p>
          </div>
        </>
      )}
    </div>
  );
};

const PromotionalHome = () => {
  const { t } = useTranslation();
  const [promoDseScores, setPromoDseScores] = useState(PROMO_DEMO_DSE_SCORES);

  const bullets = ['cognitive', 'interest', 'fast'];

  const jupasCalculatorLinkState = {
    dseScores: (promoDseScores.length ? promoDseScores : PROMO_DEMO_DSE_SCORES)
      .filter((row) => row?.subjectCode && row?.score)
      .map(({ subjectCode, score }) => ({ subjectCode, score })),
  };

  return (
    <div className="min-h-screen bg-canvas-promo text-ink">
      {/* Hero */}
      <section className="relative overflow-x-hidden">
        <div className="max-w-6xl mx-auto px-3 sm:px-5 lg:px-6 pt-6 sm:pt-10 pb-8 sm:pb-11 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-ink max-w-4xl mx-auto leading-tight">
            {t('promotionalHome.hero.titleBefore')}
            <span className="text-link">{t('promotionalHome.hero.titleHighlight')}</span>
            {t('promotionalHome.hero.titleAfter', { defaultValue: '' })}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-ink-muted max-w-2xl mx-auto leading-relaxed">
            {t('promotionalHome.hero.subtitle')}
          </p>

          <div className="mt-10 grid grid-cols-1 items-stretch gap-6 text-left max-w-5xl mx-auto lg:grid-cols-2">
            <article className={`${featureCardClass} flex flex-col`}>
              <h2 className="text-xl sm:text-2xl font-bold text-ink">
                {t('promotionalHome.hero.predictTitle')}
              </h2>
              <p className="mt-2 text-sm sm:text-base text-ink-muted leading-relaxed">
                {t('promotionalHome.hero.predictDescription')}
              </p>
              <PromotionalJupasPredictEmbed onScoresChange={setPromoDseScores} />
            </article>

            <article className={`${featureCardClass} flex flex-col`}>
              <h2 className="text-xl sm:text-2xl font-bold text-ink">
                {t('promotionalHome.hero.recommendTitle')}
              </h2>
              <p className="mt-2 text-sm sm:text-base text-ink-muted leading-relaxed">
                {t('promotionalHome.hero.recommendDescription')}
              </p>
              <PromotionalProgrammeRecommendationsEmbed />
            </article>
          </div>

          <div className="mt-10 flex justify-center py-3">
            <Link
              to="/jupas-calculator"
              state={jupasCalculatorLinkState}
              className={`relative ${promoCtaClass} px-10 py-3.5`}
            >
              <span
                className="pointer-events-none absolute inset-0 rounded-full bg-accent/45 animate-[ping_3s_cubic-bezier(0,0,0.2,1)_infinite]"
                aria-hidden
              />
              <span className="relative z-10">
                {t('promotionalHome.embed.openJupaselect')}
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust logos — continuous marquee */}
      <section
        className="py-12 sm:py-16 border-y border-edge bg-surface-muted/60 dark:bg-surface-muted"
        aria-labelledby="promo-trust-heading"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <h2 id="promo-trust-heading" className="text-center text-sm font-medium text-ink-subtle">
            {t('promotionalHome.trust.heading')}
          </h2>
        </div>
        <TrustLogoMarquee logos={INSTITUTION_LOGOS} />
      </section>

      {/* Assessment feature */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-ink leading-tight">
              {t('promotionalHome.assessment.headline')}
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed">
              {t('promotionalHome.assessment.body1')}
            </p>
            <p className="mt-4 text-ink-muted leading-relaxed">
              {t('promotionalHome.assessment.body2')}
            </p>
            <ul className="mt-8 space-y-3">
              {bullets.map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <span className="mt-1.5 h-2 w-2 rounded-full shrink-0 bg-accent" aria-hidden />
                  <span className="font-semibold text-ink">
                    {t(`promotionalHome.assessment.bullets.${key}`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <TestPromoCopy
              title={t('promotionalHome.embed.abilityTitle')}
              hint={t('promotionalHome.assessment.abilityHint')}
              description={t('promotionalHome.assessment.abilityDescription')}
              cta={
                <Link to="/ability-test" className={promoCtaClass}>
                  {t('promotionalHome.assessment.cta')}
                  <ArrowRightIcon />
                </Link>
              }
            />
            <PromotionalAbilityMenuEmbed />
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <PromotionalInterestEmbed />
            </div>
            <div className="order-1 lg:order-2">
              <TestPromoCopy
                title={t('promotionalHome.embed.interestTitle')}
                hint={t('promotionalHome.assessment.interestHint')}
                description={t('promotionalHome.assessment.interestDescription')}
                cta={
                  <Link to="/interest-test" className={promoCtaClass}>
                    {t('promotionalHome.assessment.cta')}
                    <ArrowRightIcon />
                  </Link>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blueprint timeline */}
      <section className="py-16 sm:py-24 bg-surface-muted/80" aria-labelledby="promo-blueprint-heading">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="promo-blueprint-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold text-ink">
            {t('promotionalHome.blueprint.title')}
          </h2>
          <p className="mt-4 text-ink-muted leading-relaxed">
            {t('promotionalHome.blueprint.subtitle')}
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 sm:mt-16">
          <div
            className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-edge-strong hidden md:block"
            aria-hidden
          />
          <ol className="space-y-14 sm:space-y-20 list-none p-0 m-0">
            <li>
              <TimelineStep
                align="left"
                iconKey="evaluate"
                title={t('promotionalHome.blueprint.steps.evaluate.title')}
                body={t('promotionalHome.blueprint.steps.evaluate.body')}
              />
            </li>
            <li>
              <TimelineStep
                align="right"
                iconKey="discover"
                title={t('promotionalHome.blueprint.steps.discover.title')}
                body={t('promotionalHome.blueprint.steps.discover.body')}
              />
            </li>
            <li>
              <TimelineStep
                align="left"
                iconKey="align"
                title={t('promotionalHome.blueprint.steps.align.title')}
                body={t('promotionalHome.blueprint.steps.align.body')}
              />
            </li>
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-14 sm:py-16 bg-accent/25 dark:bg-accent/10"
        aria-labelledby="promo-final-cta-heading"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="max-w-xl">
              <h2 id="promo-final-cta-heading" className="text-2xl sm:text-3xl font-bold text-ink leading-tight">
                {t('promotionalHome.finalCta.title')}
              </h2>
              <p className="mt-3 text-ink-muted">{t('promotionalHome.finalCta.subtitle')}</p>
            </div>
            <Link to="/register" className={`${promoCtaClass} self-start md:self-center`}>
              {t('promotionalHome.finalCta.button')}
              <ArrowRightIcon />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PromotionalHome;
